import { useState, useEffect, useCallback } from "react";
import { pb } from "../services/pocketbase";
import { financeiroService } from "../services/financeiroService";
import { importacaoFinanceiraService } from "../services/importacaoFinanceiraService";
import { financeiroIntegracaoService } from "../services/financeiroIntegracaoService";

export function useFinanceiro(currentUser) {
  const [loading, setLoading] = useState(false);
  const [classificacoes, setClassificacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [financiamentos, setFinanciamentos] = useState([]);
  const [tiposCompromisso, setTiposCompromisso] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  const [saldosContas, setSaldosContas] = useState({});
  const [fechamentos, setFechamentos] = useState([]);
  const [bloqueioRigido, setBloqueioRigido] = useState(() => {
    return localStorage.getItem("financeiro_bloqueio_rigido") === "true";
  });

  const toggleBloqueioRigido = useCallback(() => {
    setBloqueioRigido((prev) => {
      const next = !prev;
      localStorage.setItem("financeiro_bloqueio_rigido", String(next));
      return next;
    });
  }, []);

  const refreshFechamentos = useCallback(async () => {
    const list = await financeiroService.getFechamentos();
    setFechamentos(list);
  }, []);

  // Calcula os saldos acumulados das contas em tempo real (entradas - saídas com status = pago)
  const refreshSaldos = useCallback(async () => {
    try {
      const entries = await pb.collection("fin_lancamentos").getFullList({
        filter: 'status = "pago"',
        fields: "conta_bancaria,valor_centavos",
      });

      const sums = {};
      entries.forEach((e) => {
        if (e.conta_bancaria) {
          if (!sums[e.conta_bancaria]) sums[e.conta_bancaria] = 0;
          sums[e.conta_bancaria] += (e.valor_centavos || 0);
        }
      });
      setSaldosContas(sums);
    } catch (err) {
      console.warn("Erro ao calcular saldos das contas:", err.message);
    }
  }, []);

  // Carrega dados taxonômicos de configurações
  const refreshTaxonomy = useCallback(async () => {
    try {
      const [cls, cat, ctr, cnt, fin, tps] = await Promise.all([
        financeiroService.getClassificacoes(),
        financeiroService.getCategorias(),
        financeiroService.getCentrosCusto(),
        financeiroService.getContasBancarias(),
        financeiroService.getFinanciamentos(),
        financeiroService.getTiposCompromisso(),
      ]);
      setClassificacoes(cls);
      setCategorias(cat);
      setCentrosCusto(ctr);
      setContasBancarias(cnt);
      setFinanciamentos(fin);
      setTiposCompromisso(tps);
      await refreshSaldos();
      await refreshFechamentos();
    } catch (err) {
      console.error("Erro ao carregar dados taxonômicos do Financeiro:", err);
    }
  }, [refreshSaldos, refreshFechamentos]);

  // Carrega contagem de pendências ativas
  const refreshPendingCount = useCallback(async () => {
    try {
      const res = await importacaoFinanceiraService.getPendenciasPaginated({
        page: 1,
        perPage: 1,
        status: "pendente",
      });
      setPendingCount(res.totalItems || 0);
    } catch (err) {
      console.warn("Erro ao obter contagem de pendências financeiras:", err.message);
    }
  }, []);

  // Reconciliador de integrações externas (n8n, direct inserts de reservas/pedidos/compras) por período e origens
  const reconcileExternalIntegrations = useCallback(async (startDate, endDate, sources, onProgress) => {
    setLoading(true);
    let successCount = 0;
    try {
      console.log(`[Financeiro Reconciliacao] Iniciando varredura por período: ${startDate} até ${endDate}...`);
      if (onProgress) {
        onProgress({ current: 0, total: 100, phase: "Buscando dados no banco..." });
      }
      
      // Formata filtro de data para o PocketBase
      const filterStr = `created >= "${startDate} 00:00:00" && created <= "${endDate} 23:59:59"`;
      
      const promises = [];
      if (sources.production_orders) {
        promises.push(
          pb.collection("production_orders").getFullList({ filter: filterStr, sort: "-created" })
            .then(items => ({ type: "production_orders", items }))
            .catch(() => ({ type: "production_orders", items: [] }))
        );
      }
      if (sources.reservations) {
        promises.push(
          pb.collection("reservations").getFullList({ filter: filterStr, sort: "-created" })
            .then(items => ({ type: "reservations", items }))
            .catch(() => ({ type: "reservations", items: [] }))
        );
      }
      if (sources.supply_purchases) {
        promises.push(
          pb.collection("supply_purchases").getFullList({ filter: `${filterStr} && status != "draft"`, sort: "-created" })
            .then(items => ({ type: "supply_purchases", items }))
            .catch(() => ({ type: "supply_purchases", items: [] }))
        );
      }

      const results = await Promise.all(promises);

      // Calcula o total de registros a serem processados
      const totalItems = results.reduce((acc, r) => acc + (r.items?.length || 0), 0);
      let processed = 0;

      if (totalItems === 0) {
        if (onProgress) {
          onProgress({ current: 0, total: 0, phase: "Nenhum registro encontrado." });
        }
      }

      for (const res of results) {
        if (res.type === "production_orders") {
          for (const ord of res.items) {
            processed++;
            if (onProgress) {
              onProgress({
                current: processed,
                total: totalItems,
                phase: `Processando Pedido ${processed}/${totalItems}: ${ord.order_number || "AVULSO"}`
              });
            }
            const orderValCents = getOrderValueCents(ord);

            const result = await financeiroIntegracaoService.triggerPendingTransaction({
              origem: "production_orders",
              origemId: ord.id,
              snapshot: ord,
              dataSugerida: new Date(ord.customCreatedAt || ord.created || new Date()).toISOString().split("T")[0],
              valorSugeridoCentavos: orderValCents,
              descricaoSugerida: `Pedido Fábrica ${ord.order_number || "AVULSO"} - Cliente: ${ord.customer_name}`,
              tipoPendencia: "entrada"
            });
            if (result && result.created) {
              successCount++;
            }
          }
        } else if (res.type === "reservations") {
          for (const r of res.items) {
            processed++;
            const orderNum = r.order_metadata?.number || r.order?.number || "S/N";
            if (onProgress) {
              onProgress({
                current: processed,
                total: totalItems,
                phase: `Processando Reserva ${processed}/${totalItems}: ${orderNum}`
              });
            }
            const customerName = r.order_metadata?.customer?.name || r.customer_name || "Cliente Balcão";
            const rawVal = r.order_metadata?.payment?.total || 0;
            let orderValCents = 0;
            if (typeof rawVal === "number") {
              orderValCents = Math.round(rawVal * 100);
            } else if (typeof rawVal === "string") {
              const clean = rawVal.replace("R$", "").replace(/\./g, "").replace(",", ".").trim();
              orderValCents = Math.round(parseFloat(clean) * 100) || 0;
            }

            const result = await financeiroIntegracaoService.triggerPendingTransaction({
              origem: "reservations",
              origemId: r.id,
              snapshot: r,
              dataSugerida: new Date(r.created || new Date()).toISOString().split("T")[0],
              valorSugeridoCentavos: orderValCents,
              descricaoSugerida: `Reserva Pedido ${orderNum} - Cliente: ${customerName}`,
              tipoPendencia: "entrada"
            });
            if (result && result.created) {
              successCount++;
            }
          }
        } else if (res.type === "supply_purchases") {
          for (const pur of res.items) {
            processed++;
            if (onProgress) {
              onProgress({
                current: processed,
                total: totalItems,
                phase: `Processando Compra Insumo ${processed}/${totalItems}: Lote ${pur.id}`
              });
            }
            const result = await financeiroIntegracaoService.triggerPendingTransaction({
              origem: "supply_purchases",
              origemId: pur.id,
              snapshot: pur,
              dataSugerida: new Date(pur.created || new Date()).toISOString().split("T")[0],
              valorSugeridoCentavos: 0, // Recalculado dinamicamente no serviço a partir do custo dos insumos
              descricaoSugerida: `Compra de Insumo - Lote ${pur.id}`,
              tipoPendencia: "estorno"
            });
            if (result && result.created) {
              successCount++;
            }
          }
        }
      }

      console.log(`[Financeiro Reconciliacao] Concluído! Lançamentos importados: ${successCount}`);
      await refreshPendingCount();
      return successCount;
    } catch (err) {
      console.warn("Erro na conciliação por período:", err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshPendingCount]);

  useEffect(() => {
    if (currentUser) {
      refreshTaxonomy();
      refreshPendingCount();
    }
  }, [currentUser, refreshTaxonomy, refreshPendingCount]);

  // AÇÕES CONFIGURAÇÕES
  const createClassificacao = async (name) => {
    setLoading(true);
    try {
      const res = await financeiroService.createClassificacao(name, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const deleteClassificacao = async (id, name) => {
    setLoading(true);
    try {
      const res = await financeiroService.deleteClassificacao(id, name, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const createCategoria = async (data) => {
    setLoading(true);
    try {
      const res = await financeiroService.createCategoria(data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const updateCategoria = async (id, data) => {
    setLoading(true);
    try {
      const res = await financeiroService.updateCategoria(id, data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const createCentroCusto = async (data) => {
    setLoading(true);
    try {
      const res = await financeiroService.createCentroCusto(data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const updateCentroCusto = async (id, data) => {
    setLoading(true);
    try {
      const res = await financeiroService.updateCentroCusto(id, data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const createContaBancaria = async (data) => {
    setLoading(true);
    try {
      const res = await financeiroService.createContaBancaria(data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const updateContaBancaria = async (id, data) => {
    setLoading(true);
    try {
      const res = await financeiroService.updateContaBancaria(id, data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  // AÇÕES FINANCIAMENTOS
  const createFinanciamento = async (data) => {
    setLoading(true);
    try {
      const res = await financeiroService.createFinanciamento(data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const updateFinanciamento = async (id, data) => {
    setLoading(true);
    try {
      const res = await financeiroService.updateFinanciamento(id, data, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const deleteFinanciamento = async (id, name) => {
    setLoading(true);
    try {
      const res = await financeiroService.deleteFinanciamento(id, name, currentUser);
      await refreshTaxonomy();
      return res;
    } finally {
      setLoading(false);
    }
  };

  // AÇÕES CONFERÊNCIA
  const confirmarPendencia = async (id) => {
    setLoading(true);
    try {
      const res = await importacaoFinanceiraService.confirmarPendencia(id, currentUser);
      await refreshPendingCount();
      await refreshSaldos();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const editarEConfirmarPendencia = async (id, editedData) => {
    setLoading(true);
    try {
      const res = await importacaoFinanceiraService.editarEConfirmarPendencia(id, editedData, currentUser);
      await refreshPendingCount();
      await refreshSaldos();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const rejeitarPendencia = async (id, motivo) => {
    setLoading(true);
    try {
      const res = await importacaoFinanceiraService.rejeitarPendencia(id, motivo, currentUser);
      await refreshPendingCount();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const createTransferencia = async (data) => {
    setLoading(true);
    try {
      const res = await financeiroService.createTransferencia(data, currentUser);
      await refreshSaldos();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const fecharMes = async ({ ano, mes }) => {
    setLoading(true);
    try {
      const res = await financeiroService.fecharMes({ ano, mes }, currentUser);
      await refreshFechamentos();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const reabrirMes = async ({ ano, mes, motivo }) => {
    setLoading(true);
    try {
      const res = await financeiroService.reabrirMes({ ano, mes, motivo }, currentUser);
      await refreshFechamentos();
      return res;
    } finally {
      setLoading(false);
    }
  };

  const recalcularResumoMensal = async (ano, mes) => {
    setLoading(true);
    try {
      await financeiroService.recalcularResumoMensal(ano, mes);
    } finally {
      setLoading(false);
    }
  };

  const isPeriodoFechado = useCallback((dateStr) => {
    if (!dateStr) return false;
    const parts = dateStr.split("-");
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return fechamentos.some(f => f.ano === y && f.mes === m && !f.data_reabertura);
  }, [fechamentos]);

  const createTipoCompromisso = useCallback(async (name) => {
    try {
      setLoading(true);
      await financeiroService.createTipoCompromisso(name, currentUser);
      await refreshTaxonomy();
    } finally {
      setLoading(false);
    }
  }, [currentUser, refreshTaxonomy]);

  const updateTipoCompromisso = useCallback(async (id, payload) => {
    try {
      setLoading(true);
      await financeiroService.updateTipoCompromisso(id, payload, currentUser);
      await refreshTaxonomy();
    } finally {
      setLoading(false);
    }
  }, [currentUser, refreshTaxonomy]);

  return {
    tiposCompromisso,
    createTipoCompromisso,
    updateTipoCompromisso,
    isPeriodoFechado,
    loading,
    setLoading,
    classificacoes,
    categorias,
    centrosCusto,
    contasBancarias,
    financiamentos,
    pendingCount,
    refreshPendingCount,
    refreshTaxonomy,
    reconcileExternalIntegrations,

    // Métodos Configs
    createClassificacao,
    deleteClassificacao,
    createCategoria,
    updateCategoria,
    createCentroCusto,
    updateCentroCusto,
    createContaBancaria,
    updateContaBancaria,

    // Métodos Financiamentos
    createFinanciamento,
    updateFinanciamento,
    deleteFinanciamento,

    // Métodos Pendências
    confirmarPendencia,
    editarEConfirmarPendencia,
    rejeitarPendencia,

    // Usuário Autenticado
    user: currentUser,
    saldosContas,
    refreshSaldos,
    createTransferencia,

    // Fechamento & Resumos
    fechamentos,
    refreshFechamentos,
    fecharMes,
    reabrirMes,
    recalcularResumoMensal,
    bloqueioRigido,
    toggleBloqueioRigido,

    // Relatórios e Auditorias (Fase 6)
    getResumosAno: financeiroService.getResumosAno,
    getAuditoriaConciliacao: financeiroService.getAuditoriaConciliacao,
  };
}

function getOrderValueCents(ord) {
  // 1. Checa valores diretos
  let val = ord.total_value;
  if (val) return parseToCents(val);

  const fd = ord.full_data || {};

  // 2. Caminhos conhecidos no full_data
  val = fd.order_metadata?.payment?.total ||
        fd.order?.payment?.total ||
        fd.total_value ||
        fd.total_amount ||
        fd.total_paid_amount ||
        fd.order?.total_amount ||
        fd.order?.total_paid_amount;
  if (val) return parseToCents(val);

  // 3. Checa array de payments no root
  if (Array.isArray(fd.payments) && fd.payments.length > 0) {
    let sum = 0;
    fd.payments.forEach(p => {
      sum += (p.transaction_amount || p.total_paid_amount || p.amount || 0);
    });
    if (sum > 0) return Math.round(sum * 100);
  }

  // 4. Checa array de payments dentro de order
  if (fd.order && Array.isArray(fd.order.payments) && fd.order.payments.length > 0) {
    let sum = 0;
    fd.order.payments.forEach(p => {
      sum += (p.transaction_amount || p.total_paid_amount || p.amount || 0);
    });
    if (sum > 0) return Math.round(sum * 100);
  }

  // 5. Checa itens do pedido (order_items) no root
  if (Array.isArray(fd.order_items) && fd.order_items.length > 0) {
    let sum = 0;
    fd.order_items.forEach(item => {
      const price = item.unit_price || item.full_unit_price || 0;
      const qty = item.quantity || 1;
      sum += price * qty;
    });
    if (sum > 0) return Math.round(sum * 100);
  }

  // 6. Checa itens do pedido (order_items) dentro de order
  if (fd.order && Array.isArray(fd.order.order_items) && fd.order.order_items.length > 0) {
    let sum = 0;
    fd.order.order_items.forEach(item => {
      const price = item.unit_price || item.full_unit_price || 0;
      const qty = item.quantity || 1;
      sum += price * qty;
    });
    if (sum > 0) return Math.round(sum * 100);
  }

  return 0;
}

function parseToCents(val) {
  if (typeof val === "number") {
    return Math.round(val * 100);
  }
  if (typeof val === "string") {
    const clean = val.replace("R$", "").replace(/\./g, "").replace(",", ".").trim();
    return Math.round(parseFloat(clean) * 100) || 0;
  }
  return 0;
}
